import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { PlusCircle as PhaseAddIcon, Plus, Info } from "lucide-react";

import {
  getSelectedTournament,
  selectCategory,
} from "../../../redux/slices/tournamentSlice";
import CreateFormatModal from "../../../components/Format/Refactored/Modals/CreateFormatModal";
import useTournamentRole from "../../../hooks/useTournamentRole";

import BracketBlock from "./components/BracketBlock";
import CategoryTabs from "./components/CategoryTabs";
import CustomMatchesBlock from "./components/CustomMatchesBlock";
import EmptyState from "./components/EmptyState";
import FormatModals from "./components/FormatModals";
import GroupCard from "./components/GroupCard";
import Header from "./components/Header";
import MatchCard from "./components/MatchCard";
import PhaseHeader from "./components/PhaseHeader";
import useFormatState from "./hooks/useFormatState";
import useFormatHandlers from "./hooks/useFormatHandlers";

// Drag item type constant
const ItemTypes = {
  POSITION: "position",
};

// Draggable Position Component - represents a team position that can be dragged
const DraggablePosition = ({ position, item, blockId, phaseId, onDrop }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.POSITION,
    item: {
      sourceBlockId: blockId,
      sourcePhaseId: phaseId,
      sourcePosition: position,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }), [blockId, phaseId, position]);

  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "grab",
      }}
    >
      {item}
    </div>
  );
};

// Droppable Slot Component - represents a slot where a position can be dropped
const DroppableSlot = ({ slot, blockId, phaseId, phases, onDrop, children }) => {
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.POSITION,
    drop: (draggedItem) => {
      onDrop(draggedItem, {
        targetBlockId: blockId,
        targetPhaseId: phaseId,
        targetSlot: slot,
      });
    },
    canDrop: (draggedItem) => {
      // Prevent dropping on the same block
      return draggedItem.sourceBlockId !== blockId;
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  }), [blockId, phaseId, slot, onDrop]);

  return (
    <div
      ref={drop}
      style={{
        backgroundColor: isOver && canDrop ? "rgba(0, 231, 255, 0.1)" : "transparent",
        border: isOver && canDrop ? "1px dashed #00E7FF" : "1px solid transparent",
        borderRadius: "8px",
        transition: "all 0.2s ease",
      }}
    >
      {children}
    </div>
  );
};

const FormatRefactored = () => {
  const dispatch = useDispatch();
  const { role, permissions } = useTournamentRole();
  const [needsSpacer, setNeedsSpacer] = useState(false);

  const state = useFormatState();

  const {
    // Redux data
    selectedOrganisation,
    selectedTournament,
    selectedCategory: selectedCategoryFromRedux,

    // Core state
    phases,
    isLoading,
    selectedCategoryLabel: selectedCategory,
    setSelectedCategoryLabel: setSelectedCategory,
    openDropdownPhaseId, setOpenDropdownPhaseId,
    openEditDropdownPhaseId, setOpenEditDropdownPhaseId,
    phaseDropdownRef,

    // Template selection
    selectedTemplate,

    // Phase modals
    isAddPhaseModalOpen, setIsAddPhaseModalOpen,
    isPhaseSettingsModalOpen, setIsPhaseSettingsModalOpen,
    isCreateFormatModalOpen, setIsCreateFormatModalOpen,
    editingPhase, setEditingPhase,
    phaseInitialData, setPhaseInitialData,

    // Match state
    isMatchModalOpen, setIsMatchModalOpen,
    isDeleteMatchModalOpen, setIsDeleteMatchModalOpen,
    matchMode,
    matchFormData, setMatchFormData,
    selectedMatchGroup, setSelectedMatchGroup,

    // Group state
    isGroupModalOpen, setIsGroupModalOpen,
    isDeleteGroupModalOpen, setIsDeleteGroupModalOpen,
    groupMode,
    groupFormData, setGroupFormData,
    selectedGroup, setSelectedGroup,
    selectedPhaseForGroup,

    // Bracket state
    isBracketModalOpen, setIsBracketModalOpen,
    isDeleteBracketModalOpen, setIsDeleteBracketModalOpen,
    bracketMode,
    bracketFormData, setBracketFormData,
    selectedBracket, setSelectedBracket,
    selectedPhaseForBracket,

    // Section state
    isBracketSectionModalOpen, setIsBracketSectionModalOpen,
    selectedBracketForSection, setSelectedBracketForSection,
    sectionFormData, setSectionFormData,

    // Bracket-group state
    isBracketGroupModalOpen, setIsBracketGroupModalOpen,
    selectedBracketForGroup, setSelectedBracketForGroup,
    bracketGroupFormData, setBracketGroupFormData,

    // Bracket-match state
    isBracketMatchModalOpen, setIsBracketMatchModalOpen,
    selectedBracketForMatch, setSelectedBracketForMatch,
    bracketMatchFormData, setBracketMatchFormData,

    // Team assignment
    showTeamSelector,
    selectedTeamSlot,
    selectedBlockId,
    teamSelectorRef,

    // Progression rules
    isPhaseDeleteModalOpen, setIsPhaseDeleteModalOpen,
    phaseToDelete, setPhaseToDelete,

    // Extra points
    isExtraPointsModalOpen, setIsExtraPointsModalOpen,
    selectedGroupForExtraPoints, setSelectedGroupForExtraPoints,
    extraPointsFormData, setExtraPointsFormData,
  } = state;

  const {
    handleTemplateSelect,
    getAvailableTeams,
    getTeamById,
    handleAssignTeam,
    handleUnassignTeam,
    handleAutoAssignTeams,
    handleAutoAssignAllGroups,
    openTeamSelector,
    getProgressionRuleText,
    handleRemoveRule,
    findRuleForSlot,
    handleCreateRuleFromDrag,
    getRuleSources,
    handleAssignRuleFromClick,
    handleExecuteRules,
    openExtraPointsModal,
    handleUpdateExtraPoints,
    handleRenameBlock,
    handleRenamePhase,
    handleAddPhaseQuick,
    handleSubmitPhase,
    handleDeletePhase,
    confirmDeletePhase,
    handleTogglePhaseLock,
    openCreateMatch,
    openEditMatch,
    openDeleteMatch,
    handleSubmitMatch,
    handleConfirmDeleteMatch,
    openCreateGroup,
    openEditGroup,
    openDeleteGroup,
    handleSubmitGroup,
    handleConfirmDeleteGroup,
    openCreateBracket,
    openEditBracket,
    openDeleteBracket,
    handleSubmitBracket,
    handleConfirmDeleteBracket,
    openCreateSection,
    handleSubmitSection,
    handleSubmitBracketGroup,
    handleSubmitBracketMatch,
  } = useFormatHandlers(state);

  // Fetch full tournament data on mount to ensure categories are populated
  useEffect(() => {
    if (selectedOrganisation?.id) {
      dispatch(getSelectedTournament(selectedOrganisation.id));
    }
  }, [dispatch, selectedOrganisation?.id]);

  // Auto-select first category if none selected (via Redux dispatch)
  useEffect(() => {
    if (!selectedTournament) return;
    if (selectedCategoryFromRedux) return;

    const categories = Array.isArray(selectedTournament?.categories)
      ? selectedTournament.categories
      : [];

    if (categories.length > 0) {
      dispatch(selectCategory(categories[0]));
    }
  }, [dispatch, selectedTournament, selectedCategoryFromRedux]);

  // Group phases by name for display
  const groupByPhaseName = (phasesArray) => {
    const grouped = {};
    phasesArray.forEach((phase) => {
      const phaseName = phase.name || "Unnamed Stage";
      const phaseId = phase._id || phase.id;

      // Check if this phase has any BRACKET blocks
      const hasBracketBlock = phase.blocks?.some(b => b.type === "BRACKET");

      if (!grouped[phaseName]) {
        grouped[phaseName] = {
          ...phase, // Preserve all phase fields (matchRules, availablePitches, matchSchedule, etc.)
          id: phaseId,
          _id: phase._id,
          name: phaseName,
          bracket: hasBracketBlock ? phaseName : null,
          brackets: [],
          groups: [],
          customBlock: null,
        };
      } else if (hasBracketBlock && !grouped[phaseName].bracket) {
        grouped[phaseName].bracket = phaseName;
      }

      grouped[phaseName].brackets.push(phase);

      if (phase.blocks && phase.blocks.length > 0) {
        const normalBlocks = phase.blocks.filter((b) => !b.isCustom);
        const customBlock = phase.blocks.find((b) => b.isCustom);
        grouped[phaseName].groups.push(...normalBlocks);
        if (customBlock && !grouped[phaseName].customBlock) {
          grouped[phaseName].customBlock = customBlock;
        }
      }
    });

    return Object.values(grouped);
  };

  const groupedPhases = groupByPhaseName(phases);

  // Render "Create from scratch" section for the given phase
  const renderCreateFromScratch = (phaseGroup) => (
    <div className="">
      <h2 className="text-lg font-medium text-[#FEFCEB] mb-2">Create from scratch</h2>
      <span className="text-sm text-[#7F8585] block">
        Customize the tournament to suit your needs — add stages, set rules, and build a unique structure without restrictions.
      </span>
      <div className="flex gap-3 w-full mt-6">
        <button
          className="flex-1 px-4 py-3 rounded-full bg-[#0A2540] text-[#FEFCEB] text-sm font-medium cursor-pointer hover:bg-[#0D3152] transition-all h-11 text-center"
          onClick={() => openCreateGroup(phaseGroup)}
        >
          Add group
        </button>
        <button
          className="flex-1 px-4 py-3 rounded-full bg-[#0A2540] text-[#FEFCEB] text-sm font-medium cursor-pointer hover:bg-[#0D3152] transition-all h-11 text-center"
          onClick={() => openCreateBracket(phaseGroup)}
        >
          Add bracket
        </button>
        <button
          className="flex-1 px-4 py-3 rounded-full bg-[#0A2540] text-[#FEFCEB] text-sm font-medium cursor-pointer hover:bg-[#0D3152] transition-all h-11 text-center"
          onClick={() => openCreateMatch(phaseGroup)}
        >
          Add single match
        </button>
      </div>
    </div>
  );

  // Shared props for team slot components
  const teamSlotProps = {
    showTeamSelector,
    selectedBlockId,
    selectedTeamSlot,
    teamSelectorRef,
    getProgressionRuleText,
    getTeamById,
    getAvailableTeams,
    openTeamSelector,
    handleAssignTeam,
    handleUnassignTeam,
    handleRemoveRule,
    handleRenameBlock,
    findRuleForSlot,
    handleCreateRuleFromDrag,
    getRuleSources,
    handleAssignRuleFromClick,
    DroppableSlot,
    DraggablePosition,
    phases,
    categoryTeams: selectedCategoryFromRedux?.teams,
    canEditFormat: permissions.canEditFormat,
  };

  const isEmpty = phases.length === 0;

  // Auto-scroll during drag-and-drop (horizontal + vertical)
  const scrollContainerRef = useRef(null);
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let animationId = null;
    const EDGE_SIZE = 80;
    const SCROLL_SPEED = 4;

    // Find the vertical scroll parent — walk up from container to find the first scrollable ancestor
    let verticalScroller = null;
    let el = container.parentElement;
    while (el) {
      const style = window.getComputedStyle(el);
      if ((style.overflowY === "auto" || style.overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
        verticalScroller = el;
        break;
      }
      el = el.parentElement;
    }
    if (!verticalScroller) verticalScroller = document.documentElement;

    let lastX = 0;
    let lastY = 0;

    const tick = () => {
      let scrolled = false;
      const rect = container.getBoundingClientRect();

      // Horizontal auto-scroll on phases container
      if (lastX < rect.left + EDGE_SIZE && lastX > rect.left) {
        container.scrollLeft -= SCROLL_SPEED;
        scrolled = true;
      } else if (lastX > rect.right - EDGE_SIZE && lastX < rect.right) {
        container.scrollLeft += SCROLL_SPEED;
        scrolled = true;
      }

      // Vertical auto-scroll on content area
      if (lastY < EDGE_SIZE && lastY > 0) {
        verticalScroller.scrollTop -= SCROLL_SPEED;
        scrolled = true;
      } else if (lastY > window.innerHeight - EDGE_SIZE) {
        verticalScroller.scrollTop += SCROLL_SPEED;
        scrolled = true;
      }

      if (scrolled) animationId = requestAnimationFrame(tick);
    };

    // Listen on document so we catch drag events even when cursor is outside the phases container
    const handleDragOver = (e) => {
      lastX = e.clientX;
      lastY = e.clientY;
      cancelAnimationFrame(animationId);
      tick();
    };

    const handleDragEnd = () => {
      cancelAnimationFrame(animationId);
    };

    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("dragend", handleDragEnd);
    return () => {
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("dragend", handleDragEnd);
      cancelAnimationFrame(animationId);
    };
  }, [phases]);

  // Show spacer when team selector opens near bottom of viewport
  useEffect(() => {
    if (showTeamSelector && teamSelectorRef?.current) {
      const rect = teamSelectorRef.current.getBoundingClientRect();
      setNeedsSpacer(rect.bottom > window.innerHeight - 200);
    } else {
      setNeedsSpacer(false);
    }
  }, [showTeamSelector, teamSelectorRef]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={`flex flex-col p-3 sm:p-[18px] lg:px-8 w-full gap-5 ${isEmpty ? "h-full" : ""}`}>
        <Header hasPhases={phases.length > 0}
                canEditFormat={permissions.canEditFormat}
                onAddPhase={handleAddPhaseQuick} />
        <CategoryTabs
          categories={selectedTournament?.categories}
          selectedCategory={selectedCategory}
          onCategoryChange={(label) => {
            setSelectedCategory(label);
            const category = selectedTournament?.categories?.find(c => c.label === label);
            if (category) {
              dispatch(selectCategory(category));
            }
          }}
        />

        {phases.length > 1 && (
          <div className="flex items-start gap-2 px-1 py-1 text-xs text-[#7F8585]">
            <Info size={14} className="shrink-0 mt-0.5 text-[#00E7FF]" />
            <span>Drag a group position onto a bracket slot to create a progression rule between stages.</span>
          </div>
        )}

        {phases.length === 0 ? (
          <EmptyState onCreateFormat={permissions.canEditFormat ? () => setIsAddPhaseModalOpen(true) : null} />
        ) : (
          <div ref={scrollContainerRef} className="flex gap-[15px] items-start overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {groupedPhases.map((phaseGroup) => (
              <div
                key={phaseGroup.id}
                className="flex flex-col gap-[12px] shrink-0"
              >
                <PhaseHeader
                  phaseGroup={phaseGroup}
                  isLoading={isLoading}
                  openDropdownPhaseId={openDropdownPhaseId}
                  setOpenDropdownPhaseId={setOpenDropdownPhaseId}
                  openEditDropdownPhaseId={openEditDropdownPhaseId}
                  setOpenEditDropdownPhaseId={setOpenEditDropdownPhaseId}
                  phaseDropdownRef={phaseDropdownRef}
                  onExecuteRules={handleExecuteRules}
                  onCreateBracket={openCreateBracket}
                  onCreateGroup={openCreateGroup}
                  onCreateMatch={openCreateMatch}
                  onEditPhase={(phase, initialData) => {
                    setEditingPhase(phase);
                    setPhaseInitialData(initialData);
                    setIsPhaseSettingsModalOpen(true);
                  }}
                  onDeletePhase={handleDeletePhase}
                  onRenamePhase={handleRenamePhase}
                  onAutoAssignAll={handleAutoAssignAllGroups}
                  onTogglePhaseLock={handleTogglePhaseLock}
                  isOwner={role === "owner"}
                  canEditFormat={permissions.canEditFormat}
                />

                {/* Phase content container */}
                <div className={`p-[16px] rounded-[20px] ${phaseGroup?.groups?.length != 0 && "gap-[20px]"} bg-blue-shade-8 flex flex-col items-center`}>
                  {/* Bracket blocks display */}
                  {phaseGroup.groups.some(b => b.type === "BRACKET") && phaseGroup.groups.filter(b => b.type === "BRACKET").map((bracketBlock) => (
                    <BracketBlock
                      key={bracketBlock._id}
                      bracketBlock={bracketBlock}
                      phaseGroup={phaseGroup}
                      openDropdownPhaseId={openDropdownPhaseId}
                      setOpenDropdownPhaseId={setOpenDropdownPhaseId}
                      onCreateSection={openCreateSection}
                      onEditBracket={openEditBracket}
                      onDeleteBracket={openDeleteBracket}
                      onAutoAssign={handleAutoAssignTeams}
                      teamSlotProps={teamSlotProps}
                    />
                  ))}

                  {/* Phase-level Groups and Matches (non-bracket blocks) */}
                  <div className="flex flex-col gap-[10px] w-full">
                    {phaseGroup.groups.filter(b => b.type !== "BRACKET").length > 0 ? (
                      phaseGroup.groups
                        .filter(b => b.type !== "BRACKET")
                        .map((group) => {
                          // Check if this is a single match (KNOCKOUT with maxTeams=2)
                          const isSingleMatch = group.type === "SINGLE_MATCH" && group.maxTeams === 2;

                          if (isSingleMatch) {
                            return (
                              <MatchCard
                                key={group._id}
                                group={group}
                                phaseGroup={phaseGroup}
                                openDropdownPhaseId={openDropdownPhaseId}
                                setOpenDropdownPhaseId={setOpenDropdownPhaseId}
                                onEdit={openEditMatch}
                                onDelete={openDeleteMatch}
                                teamSlotProps={teamSlotProps}
                              />
                            );
                          } else if (group.type === "GROUP") {
                            return (
                              <GroupCard
                                key={group._id}
                                group={group}
                                phaseGroup={phaseGroup}
                                openDropdownPhaseId={openDropdownPhaseId}
                                setOpenDropdownPhaseId={setOpenDropdownPhaseId}
                                onEdit={openEditGroup}
                                onDelete={openDeleteGroup}
                                onExtraPoints={openExtraPointsModal}
                                onAutoAssign={handleAutoAssignTeams}
                                teamSlotProps={teamSlotProps}
                              />
                            );
                          } else {
                            // Other group types - placeholder
                            return (
                              <div
                                key={group._id}
                                className="p-4 bg-[#041A30] border border-[#192634] rounded-lg"
                                style={{}}
                              >
                                <div className="text-[#7F8585] text-sm">
                                  Group "{group.name}" (type: {group.type}) - not refactored yet
                                </div>
                              </div>
                            );
                          }
                        })
                    ) : null}
                  </div>

                  {/* Custom Matches block — non-draggable, styled like BracketBlock */}
                  {phaseGroup.customBlock && (
                    <div className="mt-4 w-full">
                      <CustomMatchesBlock
                        customBlock={phaseGroup.customBlock}
                        getTeamById={teamSlotProps.getTeamById}
                      />
                    </div>
                  )}

                  {/* Show "Create from scratch" only for first phase */}
                  {phaseGroup?.groups?.length === 0 && renderCreateFromScratch(phaseGroup)}
                </div>
              </div>
            ))}

            {/* Add Phase Button */}

            <div className="flex flex-col w-[200px] shrink-0">
              <button
                className="flex flex-col w-full items-center bg-transparent border border-dashed border-[#192634] rounded-xl p-6 cursor-pointer hover:border-[#00E7FF] hover:bg-[#00E7FF]/5 transition-all"
                onClick={handleAddPhaseQuick}
              >
                <p className="flex items-center gap-[5px] text-app-text text-base font-medium m-0 mb-3">
                  <Plus className="w-5 h-5" /> Add stage
                </p>
                <span className="text-app-text-secondary text-[12px] text-center block">
                  Add an extra stage if you want to assign teams based on their performance in earlier stages.
                </span>
              </button>
            </div>
          </div>
        )}

        <FormatModals
          isLoading={isLoading}
          selectedTournament={selectedTournament}
          selectedCategory={selectedCategory}
          phases={phases}
          isAddPhaseModalOpen={isAddPhaseModalOpen}
          setIsAddPhaseModalOpen={setIsAddPhaseModalOpen}
          onTemplateSelect={handleTemplateSelect}
          selectedTemplate={selectedTemplate}
          isPhaseSettingsModalOpen={isPhaseSettingsModalOpen}
          setIsPhaseSettingsModalOpen={setIsPhaseSettingsModalOpen}
          editingPhase={editingPhase}
          setEditingPhase={setEditingPhase}
          phaseInitialData={phaseInitialData}
          setPhaseInitialData={setPhaseInitialData}
          onSubmitPhase={handleSubmitPhase}
          isMatchModalOpen={isMatchModalOpen}
          setIsMatchModalOpen={setIsMatchModalOpen}
          matchMode={matchMode}
          matchFormData={matchFormData}
          setMatchFormData={setMatchFormData}
          selectedMatchGroup={selectedMatchGroup}
          setSelectedMatchGroup={setSelectedMatchGroup}
          onSubmitMatch={handleSubmitMatch}
          isDeleteMatchModalOpen={isDeleteMatchModalOpen}
          setIsDeleteMatchModalOpen={setIsDeleteMatchModalOpen}
          onConfirmDeleteMatch={handleConfirmDeleteMatch}
          isGroupModalOpen={isGroupModalOpen}
          setIsGroupModalOpen={setIsGroupModalOpen}
          groupMode={groupMode}
          groupFormData={groupFormData}
          setGroupFormData={setGroupFormData}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          selectedPhaseForGroup={selectedPhaseForGroup}
          onSubmitGroup={handleSubmitGroup}
          isDeleteGroupModalOpen={isDeleteGroupModalOpen}
          setIsDeleteGroupModalOpen={setIsDeleteGroupModalOpen}
          onConfirmDeleteGroup={handleConfirmDeleteGroup}
          isBracketModalOpen={isBracketModalOpen}
          setIsBracketModalOpen={setIsBracketModalOpen}
          bracketMode={bracketMode}
          bracketFormData={bracketFormData}
          setBracketFormData={setBracketFormData}
          selectedBracket={selectedBracket}
          setSelectedBracket={setSelectedBracket}
          selectedPhaseForBracket={selectedPhaseForBracket}
          onSubmitBracket={handleSubmitBracket}
          isDeleteBracketModalOpen={isDeleteBracketModalOpen}
          setIsDeleteBracketModalOpen={setIsDeleteBracketModalOpen}
          onConfirmDeleteBracket={handleConfirmDeleteBracket}
          isPhaseDeleteModalOpen={isPhaseDeleteModalOpen}
          setIsPhaseDeleteModalOpen={setIsPhaseDeleteModalOpen}
          phaseToDelete={phaseToDelete}
          setPhaseToDelete={setPhaseToDelete}
          onConfirmDeletePhase={confirmDeletePhase}
          isBracketGroupModalOpen={isBracketGroupModalOpen}
          setIsBracketGroupModalOpen={setIsBracketGroupModalOpen}
          selectedBracketForGroup={selectedBracketForGroup}
          setSelectedBracketForGroup={setSelectedBracketForGroup}
          bracketGroupFormData={bracketGroupFormData}
          setBracketGroupFormData={setBracketGroupFormData}
          onSubmitBracketGroup={handleSubmitBracketGroup}
          isBracketSectionModalOpen={isBracketSectionModalOpen}
          setIsBracketSectionModalOpen={setIsBracketSectionModalOpen}
          selectedBracketForSection={selectedBracketForSection}
          setSelectedBracketForSection={setSelectedBracketForSection}
          sectionFormData={sectionFormData}
          setSectionFormData={setSectionFormData}
          onSubmitSection={handleSubmitSection}
          isBracketMatchModalOpen={isBracketMatchModalOpen}
          setIsBracketMatchModalOpen={setIsBracketMatchModalOpen}
          selectedBracketForMatch={selectedBracketForMatch}
          setSelectedBracketForMatch={setSelectedBracketForMatch}
          bracketMatchFormData={bracketMatchFormData}
          setBracketMatchFormData={setBracketMatchFormData}
          onSubmitBracketMatch={handleSubmitBracketMatch}
          isExtraPointsModalOpen={isExtraPointsModalOpen}
          setIsExtraPointsModalOpen={setIsExtraPointsModalOpen}
          selectedGroupForExtraPoints={selectedGroupForExtraPoints}
          setSelectedGroupForExtraPoints={setSelectedGroupForExtraPoints}
          extraPointsFormData={extraPointsFormData}
          setExtraPointsFormData={setExtraPointsFormData}
          getTeamById={getTeamById}
          onUpdateExtraPoints={handleUpdateExtraPoints}
        />

        <CreateFormatModal
          isOpen={isCreateFormatModalOpen}
          onClose={() => setIsCreateFormatModalOpen(false)}
          templateType={selectedTemplate}
          selectedTournament={selectedTournament}
          phases={phases}
          categoryTeamCount={selectedCategoryFromRedux?.teams?.length || 0}
          onSubmit={handleSubmitPhase}
          isLoading={isLoading}
        />

        {/* Spacer to allow scrolling when team selector dropdown is open near bottom */}
        {needsSpacer && <div className="h-[350px] shrink-0" />}
      </div>
    </DndProvider>
  );
};

export default FormatRefactored;
